import { Injectable } from '@angular/core';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { createApiCallEffects } from '../../../common/store/generic.effects';
import {
  InsuranceClaimAction,
  insuranceClaimActions,
} from './insurance-claim.actions';
import { InsuranceClaimApiService } from './insurance-claim.api.service';
import { commentActions } from '../comment/comment.action';
import { insuranceClaimSelectors } from './insurance-claim.selector';

@Injectable()
export class InsuranceClaimEffects {
  constructor(
    private readonly store: Store,
    private readonly insuranceClaimApiService: InsuranceClaimApiService,
    private readonly action$: Actions<InsuranceClaimAction>,
  ) {}

  // Status transitions answer 204 NoContent: nothing usable comes back, so reload the claim itself
  // (status badge + available actions) and the comment feed (each transition writes a status-change
  // comment) — mirrors the contract effects, which refresh both on every transition.
  // Also reused after a status-history edit/delete: the history list travels inside the claim.
  private readonly refreshAfterStatusChange = () => {
    this.store.dispatch(insuranceClaimActions.getByUniqueId.refresh());
    this.store.dispatch(commentActions.getByEntityId.refresh());
  };

  getById = createApiCallEffects(
    this,
    this.action$,
    insuranceClaimActions.getById,
    this.insuranceClaimApiService.getById,
  );

  getByUniqueId = createApiCallEffects(
    this,
    this.action$,
    insuranceClaimActions.getByUniqueId,
    this.insuranceClaimApiService.getByUniqueId,
    () => {},
    () => {
      return this.store.select(
        insuranceClaimSelectors.getByUniqueId.paramsSelector,
      );
    },
  );

  createInstance = createApiCallEffects(
    this,
    this.action$,
    insuranceClaimActions.createInstance,
    this.insuranceClaimApiService.createInstance,
  );

  add = createApiCallEffects(
    this,
    this.action$,
    insuranceClaimActions.add,
    this.insuranceClaimApiService.add,
  );

  update = createApiCallEffects(
    this,
    this.action$,
    insuranceClaimActions.update,
    this.insuranceClaimApiService.update,
  );

  // The document requests travel inside the claim aggregate (InsuranceClaimModel.documents),
  // not in the document store: reloading the claim is enough to show the newly created requests.
  // No comment is written by this endpoint, so the comment feed is left alone.
  generateDocuments = createApiCallEffects(
    this,
    this.action$,
    insuranceClaimActions.generateDocuments,
    this.insuranceClaimApiService.generateDocuments,
    () => {
      this.store.dispatch(insuranceClaimActions.getByUniqueId.refresh());
    },
  );

  requestValidation = createApiCallEffects(
    this,
    this.action$,
    insuranceClaimActions.requestValidation,
    this.insuranceClaimApiService.requestValidation,
    this.refreshAfterStatusChange,
  );

  validate = createApiCallEffects(
    this,
    this.action$,
    insuranceClaimActions.validate,
    this.insuranceClaimApiService.validate,
    this.refreshAfterStatusChange,
  );

  openDossierToTransmitter = createApiCallEffects(
    this,
    this.action$,
    insuranceClaimActions.openDossierToTransmitter,
    this.insuranceClaimApiService.openDossierToTransmitter,
    this.refreshAfterStatusChange,
  );

  openClaim = createApiCallEffects(
    this,
    this.action$,
    insuranceClaimActions.openClaim,
    this.insuranceClaimApiService.openClaim,
    this.refreshAfterStatusChange,
  );

  close = createApiCallEffects(
    this,
    this.action$,
    insuranceClaimActions.close,
    this.insuranceClaimApiService.close,
    this.refreshAfterStatusChange,
  );

  reopen = createApiCallEffects(
    this,
    this.action$,
    insuranceClaimActions.reopen,
    this.insuranceClaimApiService.reopen,
    this.refreshAfterStatusChange,
  );

  reject = createApiCallEffects(
    this,
    this.action$,
    insuranceClaimActions.reject,
    this.insuranceClaimApiService.reject,
    this.refreshAfterStatusChange,
  );

  getStatusHistory = createApiCallEffects(
    this,
    this.action$,
    insuranceClaimActions.getStatusHistory,
    this.insuranceClaimApiService.getStatusHistory,
  );

  updateStatusHistory = createApiCallEffects(
    this,
    this.action$,
    insuranceClaimActions.updateStatusHistory,
    this.insuranceClaimApiService.updateStatusHistory,
    this.refreshAfterStatusChange,
  );

  deleteStatusHistory = createApiCallEffects(
    this,
    this.action$,
    insuranceClaimActions.deleteStatusHistory,
    this.insuranceClaimApiService.deleteStatusHistory,
    this.refreshAfterStatusChange,
  );

  getStatusFlow = createApiCallEffects(
    this,
    this.action$,
    insuranceClaimActions.getStatusFlow,
    this.insuranceClaimApiService.getStatusFlow,
  );

  getStatusActions = createApiCallEffects(
    this,
    this.action$,
    insuranceClaimActions.getStatusActions,
    this.insuranceClaimApiService.getStatusActions,
  );
}
